import { useAvailableServicesForCategoryQuery } from '@dieterApi/snippet/useAvailableServicesForCategoryQuery';
import Autocomplete from '@components/ui/Autocomplete/Autocomplete';
import { useShareTokenJwt } from '@app/hooks/useShareTokenJwt';
import { ISelectOption } from '@legalosApi/types/ISelectOption';
import cx from 'classnames';
import { useMemo } from 'react';
import { ScanPrefillBanner } from './ScanPrefillBanner';
import { SuggestServicePanel } from './SuggestServicePanel';

interface Props {
  category: string;
  value: string[];
  onChange: (value: string[]) => void;
}

/**
 * Compact (super-expert-mode) counterpart of {@link ServiceSelect}. Sources
 * its options from the AuditPolicySnippet catalogue, surfaces the website-scan
 * prefill banner, and offers the "suggest a missing service" panel — bringing
 * full feature parity with the page-by-page service question while keeping the
 * dense inline layout used in the super-expert form.
 */
export function CompactServiceSelect({ category, value, onChange }: Props) {
  const { waitingForShareToken } = useShareTokenJwt();
  const { data, loading } = useAvailableServicesForCategoryQuery(category);

  const options = useMemo<ISelectOption[]>(() => {
    const services = data?.availableServicesForCategory ?? [];
    return services.map((s) => ({ value: s.serviceName, label: s.serviceName, origLabel: s.serviceName }));
  }, [data]);

  const selected = useMemo(() => options.filter((o) => value.includes(o.value)), [options, value]);

  const handleToggle = (optValue: string, checked: boolean) => {
    onChange(checked ? [...value, optValue] : value.filter((v) => v !== optValue));
  };

  if ((loading || waitingForShareToken) && options.length === 0) {
    return <ScanPrefillBanner category={category} value={value} onChange={onChange} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <ScanPrefillBanner category={category} value={value} onChange={onChange} />

      {options.length > 9 ? (
        <Autocomplete
          multiple
          options={options}
          value={selected}
          onChange={(_, val) => onChange(val.map((o: ISelectOption) => o.value))}
          size="small"
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => (
            <label
              key={opt.value}
              className={cx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition-colors',
                {
                  'border-primary-root bg-primary-root/10 text-primary-root': value.includes(opt.value),
                  'border-slate-200 bg-white hover:bg-slate-50 text-slate-700': !value.includes(opt.value),
                }
              )}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300"
                checked={value.includes(opt.value)}
                onChange={(e) => handleToggle(opt.value, e.target.checked)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}

      <SuggestServicePanel category={category} value={value} onChange={onChange} />
    </div>
  );
}
